import { useState, useEffect, useRef, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';

// Shared client for the server-authoritative, socket-synced Pomodoro timer.
// Used by both the Daily Plan widget and the fullscreen TV view so they render one
// shared timer and either can control it.
const FOCUS_MIN = 25;
const BREAK_MIN = 5;

export const usePomodoro = () => {
  const socket = useSocket();
  const [state, setState] = useState(null);   // synced server state
  const [connected, setConnected] = useState(false); // real socket connection status
  const [, setTick] = useState(0);            // forces re-render each second while running
  const offsetRef = useRef(0);                // serverNow - clientNow, to correct clock skew
  const prevModeRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Simple WebAudio chime (no asset needed). Two short beeps.
  const chime = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      [0, 0.25].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = i === 0 ? 660 : 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.22);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.24);
      });
    } catch (e) { /* audio not available — silent */ }
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const onState = (s) => {
      offsetRef.current = (s.serverNow || Date.now()) - Date.now();
      if (prevModeRef.current && prevModeRef.current !== s.mode) chime();
      prevModeRef.current = s.mode;
      setState(s);
    };
    socket.on('pomodoro:state', onState);
    socket.emit('pomodoro:join');
    return () => { socket.off('pomodoro:state', onState); };
  }, [socket, chime]);

  // Track the REAL connection status and re-join on every (re)connect so the screen re-syncs.
  // `connected` must reflect the live socket, not just the socket object existing — otherwise a
  // wall-mounted TV whose socket silently dropped (token expiry, sleep/wake, network blip) keeps
  // showing a frozen timer with no hint that it's stale.
  useEffect(() => {
    if (!socket) { setConnected(false); return undefined; }
    const onConnect = () => { setConnected(true); socket.emit('pomodoro:join'); };
    const onDisconnect = () => setConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) { setConnected(true); socket.emit('pomodoro:join'); }
    return () => { socket.off('connect', onConnect); socket.off('disconnect', onDisconnect); };
  }, [socket]);

  // Safety-net re-hydrate: a passive TV can miss a broadcast (e.g. a blip between the action and a
  // reconnect). Re-asking for state every 20s while connected makes it self-correct with no input.
  useEffect(() => {
    if (!socket || !connected) return undefined;
    const id = setInterval(() => socket.emit('pomodoro:join'), 20000);
    return () => clearInterval(id);
  }, [socket, connected]);

  const running = !!state?.running;
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const send = useCallback((type) => { if (socket) socket.emit('pomodoro:action', { type }); }, [socket]);
  const setFocus = useCallback((label) => {
    if (socket && label) socket.emit('pomodoro:action', { type: 'setFocus', payload: label });
  }, [socket]);

  const mode = state?.mode || 'focus';
  const isFocus = mode === 'focus';
  const focusMin = state?.focusMin || FOCUS_MIN;
  const breakMin = state?.breakMin || BREAK_MIN;
  const completed = state?.completed || 0;
  const total = (isFocus ? focusMin : breakMin) * 60;

  let secondsLeft;
  if (state?.running && state?.endsAt) {
    secondsLeft = Math.max(0, Math.round((state.endsAt - (Date.now() + offsetRef.current)) / 1000));
  } else {
    secondsLeft = state ? state.remaining : FOCUS_MIN * 60;
  }
  const pct = ((total - secondsLeft) / total) * 100;

  return {
    connected,
    mode, isFocus, running, secondsLeft, total, pct, completed,
    focusTask: state?.focusTask || null,
    send, setFocus,
  };
};

export const fmtTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};
